"use client";

import { useEffect, useState } from "react";

interface MobileBookBarProps {
  /** Element the button scrolls to; the bar hides while it is on screen. */
  targetId: string;
  /** Same per-diver price the reservation panel shows (fees and discount applied). */
  priceLabel: string | null;
  disabled?: boolean;
}

/**
 * Phones only: keeps the price and the way to the calendar on screen. Most
 * visitors arriving from Google land on a phone, where the Reserve button
 * sits almost two screens down.
 */
const MobileBookBar: React.FC<MobileBookBarProps> = ({
  targetId,
  priceLabel,
  disabled,
}) => {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const target = document.getElementById(targetId);
    if (!target) return;
    // The target counts as "on screen" once it crosses the middle of the
    // viewport, so the bar is there on arrival and again below the calendar.
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(!entry.isIntersecting),
      { rootMargin: "-50% 0px -50% 0px" }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [targetId]);

  // The floating help buttons sit above the bar while it shows.
  useEffect(() => {
    const mobile = window.matchMedia("(max-width: 767px)");
    const sync = () => {
      if (visible && mobile.matches) {
        document.documentElement.style.setProperty("--book-bar-offset", "64px");
      } else {
        document.documentElement.style.removeProperty("--book-bar-offset");
      }
    };
    sync();
    mobile.addEventListener("change", sync);
    return () => {
      mobile.removeEventListener("change", sync);
      document.documentElement.style.removeProperty("--book-bar-offset");
    };
  }, [visible]);

  const scrollToTarget = () => {
    const target = document.getElementById(targetId);
    if (!target) return;
    const navOffset =
      parseInt(
        getComputedStyle(document.documentElement).getPropertyValue(
          "--nav-fab-offset"
        ),
        10
      ) || 72;
    window.scrollTo({
      top: target.getBoundingClientRect().top + window.scrollY - navOffset - 8,
      behavior: "smooth",
    });
  };

  return (
    <div
      className={`
        fixed inset-x-0 bottom-0 z-30 md:hidden
        border-t border-gray-200 bg-white/95 backdrop-blur
        px-4 pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))]
        shadow-[0_-4px_12px_rgba(0,0,0,0.06)]
        transition-transform duration-200
        ${visible ? "translate-y-0" : "translate-y-full pointer-events-none"}
      `}
      aria-hidden={!visible}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          {priceLabel && (
            <div className="text-lg font-bold leading-tight text-black">
              {priceLabel}
              <span className="ml-1 text-sm font-light text-neutral-500">
                / diver
              </span>
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={scrollToTarget}
          disabled={disabled}
          tabIndex={visible ? 0 : -1}
          className="flex-shrink-0 rounded-lg bg-cyan-700 px-5 py-3 font-bold text-white hover:bg-cyan-600 disabled:opacity-50"
        >
          Check dates
        </button>
      </div>
    </div>
  );
};

export default MobileBookBar;
